"use client";

import { useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { generateProductImageAlt } from "@/lib/seo/generateImageAlt";
import { SellItem } from "@/data/types";

interface ProductImageCarouselProps {
  images: string[];
  title: string;
  item?: Partial<SellItem>; // SEO를 위한 상품 정보
}

export function ProductImageCarousel({
  images,
  title,
  item,
}: ProductImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className="relative aspect-square bg-[#F4F0FF] rounded-2xl overflow-hidden flex items-center justify-center">
        <span className="text-6xl" aria-label="이미지 없음">🎵</span>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // SEO 최적화된 alt 텍스트 생성
  const getImageAlt = (index: number) => {
    if (item) {
      return generateProductImageAlt(item as SellItem, index);
    }
    return index === 0 
      ? `${title} 메인 이미지`
      : `${title} 상세 이미지 ${index + 1}`;
  };

  return (
    <div className="relative">
      {/* 메인 이미지 */}
      <div className="relative aspect-square bg-white rounded-2xl overflow-hidden shadow-sm">
        <Image
          src={images[currentIndex]}
          alt={getImageAlt(currentIndex)}
          fill
          className="object-cover"
          priority={currentIndex === 0}
          loading={currentIndex === 0 ? "eager" : "lazy"}
          sizes="100vw"
        />

        {/* 이미지 카운터 */}
        {images.length > 1 && (
          <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium">
            {currentIndex + 1} / {images.length}
          </div>
        )}

        {/* 네비게이션 버튼 */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all"
              aria-label="이전 이미지"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all"
              aria-label="다음 이미지"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* 인디케이터 점들 */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? "bg-white w-6"
                    : "bg-white/50 hover:bg-white/75"
                }`}
                aria-label={`이미지 ${index + 1}로 이동`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

